import { useEffect, useRef, useState } from 'react';
import config from '../../constants/config.js';
import './../../styles/Dashboard.css';
import { useToast } from '../../context/ToastProvider.js';
import GemstoneEditorModal from './GemstoneEditorModal.js';
import GemstoneVariantLinkerModal from './GemstoneVariantLinkerModal.js';

function GemstonesTable({
    gemstones,
    gemstoneVariantLinks,
    loading,
    pagination,
    goToPreviousPage,
    goToNextPage,
    onUpdate
}) {
    const [ openMenuIndex, setOpenMenuIndex ] = useState( null );
    const [ disableActions , setDisableActions ] = useState( false );
    const [ showModal, setShowModal ] = useState( false );
    const [ showVariantLinkerModal, setShowVariantLinkerModal ] = useState( false );
    const [ gemstoneToEdit, setGemstoneToEdit ] = useState( null );

    const [ shopSelectedForVariantLink , setShopSelectedForVariantLink ] = useState( "" );
    const [ productIdSelectedForVariantLink , setProductIdSelectedForVariantLink ] = useState( "" );
    const [ gemstoneIdSelectedForVariantLink , setGemstoneIdSelectedForVariantLink ] = useState( "" );
    const [ gemstoneNameSelectedForVariantLink , setGemstoneNameSelectedForVariantLink ] = useState( "" );

    const menuRefs = useRef( [] );
    const addToast = useToast();

    function toggleMenu( index ) {
        setOpenMenuIndex( ( prevIndex ) => ( prevIndex === index ? null : index ) );
    }

    async function removeGemstone( gemstoneId , menuIndex ) {
        setDisableActions( true );
        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/gemstones/${encodeURIComponent(gemstoneId)}`;
            const requestSettings = {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                }
            };

            const response = await fetch( url , requestSettings );
            addToast( `Succesfully removed the gemstone`, false , 5000 );
            toggleMenu( menuIndex );

            if ( typeof ( onUpdate ) === 'function' ) {
                onUpdate();
            }
        } catch ( error ) {
            console.log( error );
        } finally {
            setDisableActions( false )
        }
    }

    async function toggleGemstone( gemstoneId , newStatus , menuIndex ) {
        setDisableActions( true );
        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/gemstones/${encodeURIComponent( gemstoneId )}/${encodeURIComponent( newStatus )}`;
            const requestSettings = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify( {} )
            };

            const response = await fetch( url , requestSettings );
            addToast( `Toggled Gemstone. New status: "${newStatus}"`, false , 5000 );
            toggleMenu( menuIndex );

            if ( typeof ( onUpdate ) === 'function' ) {
                onUpdate();
            }
        } catch ( error ) {
            console.log( error );
        } finally {
            setDisableActions( false )
        }
    }

    function formatTimeAgo( timestamp ) {
        const now = new Date();
        const lastModified = new Date(timestamp);
        
        if ( isNaN( lastModified.getTime() ) ) {
            return "NEVER";
        }

        const diffInSeconds = Math.floor((now - lastModified) / 1000);
    
        if (diffInSeconds < 60) {
            return `${diffInSeconds} sec(s) ago`;
        } else if (diffInSeconds < 3600) {
            return `${Math.floor(diffInSeconds / 60)} min(s) ago`;
        } else if (diffInSeconds < 86400) {
            return `${Math.floor(diffInSeconds / 3600)} hour(s) ago`;
        } else if (diffInSeconds < 432000) {
            return `${Math.floor(diffInSeconds / 86400)} day(s) ago`;
        } else {
            return lastModified.toLocaleDateString();
        }
    }

    function formatCurrency( money ) {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        });

        return formatter.format( money );
    }

    function getLinkingInfoForGemstone( links , currentGemstoneId ) {
        const linkData = {
            add_links: [],
            remove_links: []
        };

        for ( let i = 0; i < links.length; i++ ) {
            // cannot show menu when not linked to a product at all
            if ( !links[i].store_connected_to_product ) {
                continue;
            }

            let ableToLinkTo = false;
            let productId = -1;
            for ( let j = 0; j < links[i].variant_gemstone_without_links.length; j++ ) {
                const idToCheck = links[i].variant_gemstone_without_links[j].gemstone_id;
                if ( idToCheck == currentGemstoneId ) {
                    ableToLinkTo = true;
                    productId = parseInt( links[i].variant_gemstone_without_links[j].product_id_to_link_to );
                    break;
                }
            }

            if ( ableToLinkTo ) {
                linkData.add_links.push({
                    title: `Link a variant on ${links[i].store_details.nickname}`,
                    product_id: productId,
                    shop: links[i].store_details.shop,
                    originalLinkData: links[i]
                });
            } else {
                let variantId = -1;
                for ( let j = 0; j < links[i].variant_gemstone_links.length; j++ ) {
                    const idToCheck = links[i].variant_gemstone_links[j].gemstone_id;
                    if ( idToCheck == currentGemstoneId ) {
                        variantId = parseInt( links[i].variant_gemstone_links[j].id );
                        break;
                    }
                }

                linkData.remove_links.push({
                    title: `Remove variant link on ${links[i].store_details.nickname}`,
                    variant_id: variantId,
                    shop: links[i].store_details.shop,
                    originalLinkData: links[i]
                });
            }
        }

        return linkData;
    }

    function toggleVariantLinkerModal( shop , productId , gemstoneId , gemstoneName ) {
        setShopSelectedForVariantLink( shop );
        setProductIdSelectedForVariantLink( productId );
        setGemstoneIdSelectedForVariantLink( gemstoneId );
        setGemstoneNameSelectedForVariantLink( gemstoneName )
        setShowVariantLinkerModal( true );
    }

    async function removeVariantLink( shop , variantId ) {
        setDisableActions( true );

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/links/gemstones/variants/${encodeURIComponent( shop )}/${encodeURIComponent( variantId )}`;
            const requestSettings = {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                }
            };

            const response = await fetch( url , requestSettings );
            const data = await response.json();
            if ( data?.errors?.length > 0 ) {
                addToast( `Failed to remove gemstone variant link - ${data?.errors[0].message}`, true , 5000 );
            } else {
                addToast( `Removed Gemstone Variant Link!`, false , 5000 );
            }
        } catch ( error ) {
            console.log( error );
        }

        if ( typeof onUpdate === 'function' ) {
            onUpdate();
        }
        setDisableActions( false );
    }

    useEffect(() => {
        function handleClickOutside(event) {
            if ( openMenuIndex !== null && menuRefs.current[openMenuIndex] && 
                !menuRefs.current[ openMenuIndex ].contains(event.target)) {
                setOpenMenuIndex(null);
            }
        }

        if ( openMenuIndex !== null ) {
            document.addEventListener( "mousedown", handleClickOutside );
        }

        return () => {
            document.removeEventListener( "mousedown", handleClickOutside );
        };
    }, [ openMenuIndex ] );

    if ( loading ) {
        return(
            <div className="product-table-skeleton"></div>
        )
    }

    return( 
        <div>
            <div className="dashboard-table">
                <table id="recent-products">
                    <thead>
                        <tr>
                            <th>GEMSTONE NAME</th>
                            <th>PREVIEW</th>
                            <th>PRICE</th>
                            <th>STATUS</th>
                            <th>LAST MODIFIED</th>
                            <th>{/* ACTIONS */}</th>
                        </tr>
                    </thead>
                    <tbody>
                        { 
                            gemstones?.length > 0 ? 
                                gemstones?.map( ( row , index ) => {
                                    const linkingData = getLinkingInfoForGemstone( gemstoneVariantLinks , row.id );

                                    return(
                                        <tr className="product-row" key={ index }>
                                            <td>
                                                <div className="product-details">
                                                    <div className="product-title">
                                                        {row?.gemstone_name || ""}
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="gemstone-image bold">
                                                    <img 
                                                        src={row?.gemstone_image_url || '#'}
                                                        alt={row?.gemstone_name || ""}
                                                        width={30}
                                                        height={30}
                                                        loading={"lazy"}
                                                    />

                                                </div>
                                            </td>
                                            
                                            <td>
                                                <div className="price normal">
                                                    { formatCurrency( row.gemstone_price ) }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="status normal">
                                                    { row?.status || "" }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="last-editor normal">
                                                    { formatTimeAgo( row?.last_modified ) }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="actions" ref={(el) => menuRefs.current[index] = el}>
                                                    <button className="more-options" onClick={() => toggleMenu( index )}>
                                                        ...
                                                    </button>

                                                    { openMenuIndex === index && (
                                                        <ul className="options-menu">
                                                            <li className="option-menu-li">
                                                                <button disabled={disableActions} className="option-action" onClick={ () => {
                                                                    setGemstoneToEdit( row );
                                                                    setShowModal( true );
                                                                } }>
                                                                    Edit
                                                                </button>
                                                            </li>

                                                            {/* Enable / Disable */}
                                                            { row?.status === "Enabled" &&
                                                                <li className="option-menu-li">
                                                                    <button disabled={disableActions} className="option-action" onClick={ () => toggleGemstone( row.id , "Disabled" , index ) }>
                                                                        Disable
                                                                    </button>
                                                                </li>
                                                            }

                                                            { row?.status === "Disabled" &&
                                                                <li className="option-menu-li">
                                                                    <button disabled={disableActions} className="option-action" onClick={ () => toggleGemstone( row.id , "Enabled" , index ) }>
                                                                        Enable
                                                                    </button>
                                                                </li>
                                                            }
                                                            
                                                            { linkingData.add_links.map( ( ref , _index ) => {
                                                                return(
                                                                    <li key={_index} className="option-menu-li">
                                                                        <button 
                                                                            disabled={disableActions} 
                                                                            className="option-action" 
                                                                            onClick={ () => {
                                                                                toggleVariantLinkerModal(
                                                                                    ref.shop,
                                                                                    ref.product_id,
                                                                                    row.id,
                                                                                    row.gemstone_name
                                                                                );
                                                                            } }>
                                                                            {ref.title}
                                                                        </button>
                                                                    </li>
                                                                )
                                                            })}

                                                            { linkingData.remove_links.map( ( ref , _index ) => {
                                                                return(
                                                                    <li key={_index} className="option-menu-li">
                                                                        <button 
                                                                            disabled={disableActions} 
                                                                            className="option-action" 
                                                                            onClick={ () => {
                                                                                removeVariantLink( 
                                                                                    ref.shop,
                                                                                    ref.variant_id
                                                                                );
                                                                            }}>
                                                                            {ref.title}
                                                                        </button>
                                                                    </li>
                                                                )
                                                            })}

                                                            <li className="option-menu-li">
                                                                <button disabled={disableActions} className="option-action" onClick={ () => removeGemstone( row.id , index ) }>
                                                                    Remove
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })
                            :
                            <tr>
                                <td colSpan="5">No Gemstones Found</td>
                            </tr>
                        }
                    </tbody>
                </table>

                { pagination != null && pagination?.totalPages > 1 &&
                    <div className="pagination">
                        <button onClick={goToPreviousPage} disabled={pagination.currentPage === 1}>
                            Previous
                        </button>
                        <span>Page {pagination.currentPage} of {pagination.totalPages}</span>
                        <button onClick={goToNextPage} disabled={pagination.currentPage === pagination.totalPages}>
                            Next
                        </button>
                    </div>
                }

                { showModal && ( 
                    <GemstoneEditorModal
                        isOpen={ showModal }
                        onClose={() => { 
                            setShowModal(false)
                        }}
                        onAdd={ () => { 
                            if ( typeof ( onUpdate ) === 'function' ) {
                                onUpdate();
                            }
                        }}
                        initialData={ gemstoneToEdit }
                    />
                )}

                { showVariantLinkerModal && 
                    <GemstoneVariantLinkerModal
                        isOpen={ showVariantLinkerModal }
                        onClose={ () => {
                            setShowVariantLinkerModal(false)
                        }}
                        onUpdate={ () => {
                            if ( typeof ( onUpdate ) === 'function' ) {
                                onUpdate();
                            }
                        }}
                        shop={ shopSelectedForVariantLink }
                        productId={ productIdSelectedForVariantLink }
                        gemstoneId={ gemstoneIdSelectedForVariantLink }
                        gemstoneName={ gemstoneNameSelectedForVariantLink }
                    />
                }
            </div>
        </div>
    );
}

export default GemstonesTable;